import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Backdrop,
  LinearProgress,
  IconButton,
} from "@mui/material";
import {
  Inventory,
  Kitchen,
  Checkroom,
  Image,
  Edit,
} from "@mui/icons-material";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  createBoxRate,
  getBoxRate,
  updateBoxRate,
} from "../../redux/action/rate";
import { useDispatch } from "react-redux";
import { getCarrier } from "../../redux/action/carrier";

const BoxRates: React.FC = () => {
  const theme = useTheme();

  const [smallBox, setSmallBox] = useState("");
  const [mediumBox, setMediumBox] = useState("");
  const [largeBox, setLargeBox] = useState("");
  const [dishPack, setDishPack] = useState("");
  const [mirrorCartons, setMirrorCartons] = useState("");
  const [wardrobeBoxes, setWardrobeBoxes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [isBoxRateExists, setIsBoxRateExists] = useState(false);
  const [editableField, setEditableField] = useState<string | null>(null);
  const email = localStorage.getItem("email");
  const [carrierId, setCarrierId] = useState(null);

  const handleSubmit = async () => {
    setIsLoading(true);
    setLoadingMessage("Saving changes. Please wait...");
    const boxRateData = {
      carrier_id: carrierId,
      small_box: smallBox,
      medium_box: mediumBox,
      large_box: largeBox,
      dish_pack: dishPack,
      mirror_cartons: mirrorCartons,
      wardrobe_box: wardrobeBoxes,
    };

    try {
      let response;
      if (isBoxRateExists) {
        response = await dispatch<any>(updateBoxRate(boxRateData));
      } else {
        response = await dispatch<any>(createBoxRate(boxRateData));
      }
      const result = unwrapResult(response);
      setLoadingMessage("Changes saved successfully!");
      setEditableField(null);
      setIsBoxRateExists(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    } catch (err: any) {
      setLoadingMessage("Failed to save changes.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (email) {
      dispatch<any>(getCarrier(email))
        .then(unwrapResult)
        .then((res: any) => {
          const id = res.id;
          localStorage.setItem("CarrierId", id);
          setCarrierId(id);
        })
        .catch((err: any) => {
          console.log(err, "error-------");
        });
    }
  }, [email]);
  useEffect(() => {
    if (carrierId) {
      const carrierIdNumber = parseInt(carrierId, 10);
      if (!isNaN(carrierIdNumber)) {
        dispatch<any>(getBoxRate(carrierIdNumber))
          .then(unwrapResult)
          .then((res: any) => {
            if (res) {
              setIsBoxRateExists(true);
              setSmallBox(res.small_box);
              setMediumBox(res.medium_box);
              setLargeBox(res.large_box);
              setDishPack(res.dish_pack);
              setMirrorCartons(res.mirror_cartons);
              setWardrobeBoxes(res.wardrobe_box);
            } else {
              setIsBoxRateExists(false);
            }
          })
          .catch((err: any) => {
            setLoadingMessage("Failed to fetch carrier data.");
          });
      }
    }
  }, [carrierId]);
  const handleNumericInputChange = (e:any, setter:any) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setter(value);
    }
  };
  return (
    <Box sx={{ p: 3 }}>
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Typography
        sx={{
          fontWeight: "bold",
          mb: 4,
          fontSize: isMobile ? "1.5rem" : "2rem",
        }}
      >
        Box Rates
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
        }}
      >
        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Small Box"
            variant="outlined"
            value={smallBox}
            onChange={(e) => handleNumericInputChange(e,setSmallBox)}
            disabled={isBoxRateExists && editableField !== "smallBox"}
            InputProps={{
              startAdornment: (
                <Inventory sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isBoxRateExists && (
                <IconButton
                  onClick={() => setEditableField("smallBox")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Medium Box"
            variant="outlined"
            value={mediumBox}
            onChange={(e) => handleNumericInputChange(e,setMediumBox)}
            disabled={isBoxRateExists && editableField !== "mediumBox"}
            InputProps={{
              startAdornment: (
                <Inventory sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isBoxRateExists && (
                <IconButton
                  onClick={() => setEditableField("mediumBox")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Large Box"
            variant="outlined"
            value={largeBox}
            onChange={(e) => handleNumericInputChange(e,setLargeBox)}
            disabled={isBoxRateExists && editableField !== "largeBox"}
            InputProps={{
              startAdornment: (
                <Inventory sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isBoxRateExists && (
                <IconButton
                  onClick={() => setEditableField("largeBox")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Dish Pack"
            variant="outlined"
            value={dishPack}
            onChange={(e) => handleNumericInputChange(e,setDishPack)}
            disabled={isBoxRateExists && editableField !== "dishPack"}
            InputProps={{
              startAdornment: (
                <Kitchen sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isBoxRateExists && (
                <IconButton
                  onClick={() => setEditableField("dishPack")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Mirror Cartons"
            variant="outlined"
            value={mirrorCartons}
            onChange={(e) => handleNumericInputChange(e,setMirrorCartons)}
            disabled={isBoxRateExists && editableField !== "mirrorCartons"}
            InputProps={{
              startAdornment: (
                <Image sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isBoxRateExists && (
                <IconButton
                  onClick={() => setEditableField("mirrorCartons")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            flex: "1 1 45%",
            minWidth: "240px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            label="Wardrobe Boxes"
            variant="outlined"
            value={wardrobeBoxes}
            onChange={(e) => handleNumericInputChange(e,setWardrobeBoxes)}
            disabled={isBoxRateExists && editableField !== "wardrobeBoxes"}
            InputProps={{
              startAdornment: (
                <Checkroom sx={{ mr: 1, color: theme.palette.primary.main }} />
              ),
              endAdornment: isBoxRateExists && (
                <IconButton
                  onClick={() => setEditableField("wardrobeBoxes")}
                  sx={{ ml: 1 }}
                >
                  <Edit />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{ px: 4, py: 2,width: isMobile ? "100%" : "200px" }}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default BoxRates;
